import { useEffect, useRef, useState, type FormEvent, type ReactNode } from 'react';

import { PasswordField, PremiumField, Spinner } from '@kilat/ui';

import { useAuth, type GoogleSignInOutcome } from '../lib/use-auth.js';
import { passwordFieldLabels } from '../lib/form-labels.js';
import { useI18n } from '../lib/use-i18n.js';
import { usePlatformBrand } from '../lib/use-platform-brand.js';
import { logger } from '../lib/logger.js';
import { loginErrorKey, resetErrorKey } from '../lib/login-errors.js';

/** The account-link branch of a Google attempt — email + pending credential. */
type LinkState = Extract<GoogleSignInOutcome, { status: 'link-with-password' }>;

/**
 * Returning-agent login for the sales-agent PWA.
 *
 * Rendered by `App`'s `AuthGate` whenever `!user` — it REPLACES the old dead-end
 * "You're signed out. Phone OTP sign-in lands in a later sprint." message that
 * left an agent with no way back in once their first Firebase session ended.
 *
 * Mobile-first surface (this is a PWA): a single full-width column below the
 * shell's slim brand + LocaleSwitcher header. "Continue with Google" is primary;
 * email + password is the manual path; a prominent "set / reset password" link
 * covers first-time agents, who set their password via the reset link
 * `agentBootstrap` implies. On success the existing
 * `onAuthStateChanged`/`onIdTokenChanged` flow in `AuthProvider` takes over and
 * routing renders per claims — same as the `#signInToken` handoff, which is
 * untouched. Every state is explicit with custom copy and a next action
 * ([[feedback-no-dead-ends]], [[feedback-custom-crafted-ui-text]]).
 */
export function SignInScreen() {
  const [mode, setMode] = useState<'signIn' | 'reset'>('signIn');

  return (
    <main
      style={{
        minHeight: '100dvh',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'flex-start',
        padding:
          'var(--space-6) var(--space-4) max(env(safe-area-inset-bottom, 0px), var(--space-8))',
        background:
          'radial-gradient(80% 50% at 90% -10%, rgba(13,122,79,0.14), transparent 60%),' +
          'radial-gradient(70% 45% at -10% 0%, rgba(245,158,11,0.10), transparent 60%),' +
          'var(--color-surface-sunken, #fafaf7)',
      }}
    >
      <div style={{ width: '100%', maxWidth: 440, display: 'grid', gap: 'var(--space-5)' }}>
        <Hero />
        {mode === 'signIn' ? (
          // eslint-disable-next-line i18next/no-literal-string -- view-mode enum, not copy
          <SignInCard onForgot={() => setMode('reset')} />
        ) : (
          // eslint-disable-next-line i18next/no-literal-string -- view-mode enum, not copy
          <ResetCard onBack={() => setMode('signIn')} />
        )}
      </div>
    </main>
  );
}

// ── Hero ────────────────────────────────────────────────────────────────

function Hero() {
  const t = useI18n();
  const brand = usePlatformBrand();
  return (
    <header style={{ display: 'grid', gap: 'var(--space-2)', textAlign: 'center' }}>
      <div
        aria-hidden
        style={{
          width: 44,
          height: 44,
          borderRadius: 14,
          margin: '0 auto',
          background: 'linear-gradient(135deg, var(--color-primary, #0d7a4f), #16956c)',
          color: '#fff',
          display: 'grid',
          placeItems: 'center',
          fontWeight: 700,
          fontSize: 20,
          boxShadow: 'var(--shadow-2, 0 8px 20px rgba(13,122,79,0.28))',
        }}
      >
        {brand.shortName.slice(0, 1)}
      </div>
      <h1 style={{ margin: 'var(--space-2) 0 0 0', fontSize: 'var(--text-h1)', fontWeight: 600 }}>
        {t.t('agent.login.title', { brand: brand.shortName })}
      </h1>
      <p
        style={{
          margin: 0,
          fontSize: 'var(--text-body)',
          lineHeight: 1.5,
          color: 'var(--color-on-surface-muted)',
        }}
      >
        {t.t('agent.login.subtitle')}
      </p>
    </header>
  );
}

// ── Sign-in card ────────────────────────────────────────────────────────

function SignInCard({ onForgot }: { readonly onForgot: () => void }) {
  const t = useI18n();
  const brand = usePlatformBrand();
  const { signInWithEmail, signInWithGoogle } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [googleBusy, setGoogleBusy] = useState(false);
  const [errorKey, setErrorKey] = useState<string | null>(null);
  const [link, setLink] = useState<LinkState | null>(null);

  async function handleGoogle(): Promise<void> {
    setErrorKey(null);
    setGoogleBusy(true);
    const outcome = await signInWithGoogle();
    if (outcome.status === 'success') return; // unmounts on the auth-state flip
    if (outcome.status === 'link-with-password') {
      // The email already has a password account → collect it once and link,
      // rather than dead-ending on the Firebase conflict.
      setLink(outcome);
      setGoogleBusy(false);
      return;
    }
    if (outcome.errorKey) setErrorKey(outcome.errorKey); // null = benign popup cancel
    setGoogleBusy(false);
  }

  async function handleSubmit(e: FormEvent<HTMLFormElement>): Promise<void> {
    e.preventDefault();
    setErrorKey(null);
    setSubmitting(true);
    try {
      await signInWithEmail(email.trim(), password);
      // Success: AuthProvider's onAuthStateChanged flips `user`, AuthGate
      // re-renders the app and this component unmounts — no manual nav needed.
    } catch (err) {
      // Map every failure to ONE generic, non-leaking line. We deliberately do
      // NOT probe `fetchSignInMethodsForEmail` on the failure path: telling an
      // unauthenticated guesser "this email signs in with Google" would leak
      // whether an email is a real Kilat agent account — an account-enumeration
      // oracle. The legitimate Google→password LINK flow still knows the email
      // is Google-linked, but only because Firebase itself told it (the
      // `account-exists-with-different-credential` branch in use-auth.tsx).
      const key = loginErrorKey(err);
      setErrorKey(key);
      logger.warn('agent sign-in failed', { key });
      setSubmitting(false);
    }
  }

  if (link) {
    return (
      <LinkPanel
        link={link}
        onBack={() => {
          setLink(null);
          setErrorKey(null);
        }}
      />
    );
  }

  return (
    <Card>
      <GoogleButton busy={googleBusy} onClick={handleGoogle}>
        {googleBusy ? t.t('agent.login.googleSubmitting') : t.t('agent.login.google')}
      </GoogleButton>

      <OrDivider label={t.t('agent.login.orEmail')} />

      <form onSubmit={handleSubmit} style={{ display: 'grid', gap: 'var(--space-4)' }}>
        <PremiumField
          label={t.t('agent.login.emailLabel')}
          type="email"
          value={email}
          onChange={setEmail}
          autoComplete="email"
          autoCapitalize="none"
          placeholder={t.t('agent.login.emailPlaceholder')}
          required
          icon="mail"
        />
        <div style={{ display: 'grid', gap: 8 }}>
          <PasswordField
            labels={passwordFieldLabels(t.t)}
            label={t.t('agent.login.passwordLabel')}
            value={password}
            onChange={setPassword}
            autoComplete="current-password"
            placeholder={t.t('agent.login.passwordPlaceholder')}
            required
          />
          <button
            type="button"
            onClick={onForgot}
            style={linkButtonStyle}
            data-testid="forgot-link"
          >
            {t.t('agent.login.forgot')}
          </button>
        </div>

        {errorKey ? <ErrorAlert>{t.t(errorKey)}</ErrorAlert> : null}

        <SubmitButton submitting={submitting}>
          {submitting ? t.t('agent.login.submitting') : t.t('agent.login.submit')}
        </SubmitButton>
      </form>

      <Divider />

      <p style={footerLineStyle}>
        {t.t('agent.login.noAccount', { brand: brand.shortName })}{' '}
        <a href={`https://${brand.domains.marketing}/become-an-agent`} style={inlineLinkStyle}>
          {t.t('agent.login.createAccount')}
        </a>
      </p>
    </Card>
  );
}

// ── Google link panel (account-model resolution) ─────────────────────────

/**
 * Shown when "Continue with Google" hit `account-exists-with-different-credential`
 * — i.e. the agent already signs in with a password. They enter that password
 * ONCE; we sign in and `linkWithCredential` attaches Google, so every future tap
 * is one-touch. This is the correct fix for the reason Google was first deferred.
 */
function LinkPanel({ link, onBack }: { readonly link: LinkState; readonly onBack: () => void }) {
  const t = useI18n();
  const { linkGoogleAfterPassword } = useAuth();
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [errorKey, setErrorKey] = useState<string | null>(null);

  // `linkGoogleAfterPassword` signs in FIRST (to prove the password) and only
  // THEN attaches Google. The sign-in flips auth state, which can unmount this
  // panel before `linkWithCredential` settles. Guard every post-await setState
  // so a link failure landing after unmount doesn't setState on a dead
  // component. If we're still mounted we surface the error; if not, the agent is
  // already signed in and the best-effort Google link simply didn't complete.
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  async function handleSubmit(e: FormEvent<HTMLFormElement>): Promise<void> {
    e.preventDefault();
    setErrorKey(null);
    setSubmitting(true);
    try {
      await linkGoogleAfterPassword(link.email, password, link.pendingCredential);
      // Success unmounts via the auth-state flip.
    } catch (err) {
      const key = loginErrorKey(err);
      logger.warn('agent google account-link failed', { key });
      if (!mountedRef.current) return;
      setErrorKey(key);
      setSubmitting(false);
    }
  }

  return (
    <Card>
      <CardHeader title={t.t('agent.login.linkTitle')}>
        {t.t('agent.login.linkBody', { email: link.email })}
      </CardHeader>

      <form onSubmit={handleSubmit} style={{ display: 'grid', gap: 'var(--space-4)' }}>
        <PasswordField
          labels={passwordFieldLabels(t.t)}
          label={t.t('agent.login.passwordLabel')}
          value={password}
          onChange={setPassword}
          autoComplete="current-password"
          placeholder={t.t('agent.login.passwordPlaceholder')}
          required
        />

        {errorKey ? <ErrorAlert>{t.t(errorKey)}</ErrorAlert> : null}

        <SubmitButton submitting={submitting}>
          {submitting ? t.t('agent.login.linkSubmitting') : t.t('agent.login.linkSubmit')}
        </SubmitButton>
      </form>

      <button
        type="button"
        onClick={onBack}
        style={{ ...linkButtonStyle, justifySelf: 'center', marginTop: 'var(--space-4)' }}
      >
        ← {t.t('agent.login.linkBack')}
      </button>
    </Card>
  );
}

// ── Reset card ──────────────────────────────────────────────────────────

function ResetCard({ onBack }: { readonly onBack: () => void }) {
  const t = useI18n();
  const { sendPasswordReset } = useAuth();
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [errorKey, setErrorKey] = useState<string | null>(null);
  const [sent, setSent] = useState(false);

  async function handleSubmit(e: FormEvent<HTMLFormElement>): Promise<void> {
    e.preventDefault();
    setErrorKey(null);
    setSubmitting(true);
    try {
      await sendPasswordReset(email.trim());
      setSent(true);
    } catch (err) {
      // `resetErrorKey` returns null for unknown-email → we STILL show the
      // confirmation so the form never reveals whether an account exists.
      const key = resetErrorKey(err);
      if (key === null) {
        setSent(true);
      } else {
        setErrorKey(key);
        logger.warn('agent password-reset failed', { key });
        setSubmitting(false);
      }
    }
  }

  if (sent) {
    return (
      <Card>
        <CardHeader title={t.t('agent.login.resetSentTitle')}>
          {t.t('agent.login.resetSentBody', { email: email.trim() })}
        </CardHeader>
        <button type="button" onClick={onBack} style={{ ...linkButtonStyle, justifySelf: 'start' }}>
          ← {t.t('agent.login.resetBack')}
        </button>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader title={t.t('agent.login.resetTitle')}>
        {t.t('agent.login.resetSubtitle')}
      </CardHeader>

      <form onSubmit={handleSubmit} style={{ display: 'grid', gap: 'var(--space-4)' }}>
        <PremiumField
          label={t.t('agent.login.emailLabel')}
          type="email"
          value={email}
          onChange={setEmail}
          autoComplete="email"
          autoCapitalize="none"
          placeholder={t.t('agent.login.emailPlaceholder')}
          autoFocus
          required
          icon="mail"
        />

        {errorKey ? <ErrorAlert>{t.t(errorKey)}</ErrorAlert> : null}

        <SubmitButton submitting={submitting}>
          {submitting ? t.t('agent.login.resetSubmitting') : t.t('agent.login.resetSubmit')}
        </SubmitButton>
      </form>

      <button
        type="button"
        onClick={onBack}
        style={{ ...linkButtonStyle, justifySelf: 'center', marginTop: 'var(--space-4)' }}
      >
        ← {t.t('agent.login.resetBack')}
      </button>
    </Card>
  );
}

// ── Shared building blocks ──────────────────────────────────────────────

function Card({ children }: { readonly children: ReactNode }) {
  return (
    <div
      style={{
        background: 'var(--color-surface, #ffffff)',
        borderRadius: 'var(--radius-lg)',
        border: '1px solid var(--color-border)',
        padding: 'var(--space-6)',
        boxShadow:
          '0 1px 2px var(--tint-subtle), 0 8px 24px var(--color-border), 0 24px 60px rgba(13,122,79,0.05)',
        display: 'grid',
        gap: 'var(--space-5)',
      }}
    >
      {children}
    </div>
  );
}

function CardHeader({ title, children }: { readonly title: string; readonly children: ReactNode }) {
  return (
    <header style={{ display: 'grid', gap: 'var(--space-2)' }}>
      <h2 style={{ margin: 0, fontSize: 'var(--text-h2)', fontWeight: 600 }}>{title}</h2>
      <p
        style={{
          margin: 0,
          fontSize: 'var(--text-body)',
          lineHeight: 1.5,
          color: 'var(--color-on-surface-muted)',
        }}
      >
        {children}
      </p>
    </header>
  );
}

function GoogleButton({
  busy,
  onClick,
  children,
}: {
  readonly busy: boolean;
  readonly onClick: () => void;
  readonly children: ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={busy}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 12,
        width: '100%',
        minHeight: 'var(--touch-target)',
        padding: '13px 18px',
        borderRadius: 'var(--radius-md)',
        border: '1px solid var(--color-border-strong)',
        background: 'var(--color-surface, #ffffff)',
        color: 'var(--color-on-surface)',
        fontFamily: 'inherit',
        fontSize: 'var(--text-body)',
        fontWeight: 600,
        cursor: busy ? 'wait' : 'pointer',
        opacity: busy ? 0.75 : 1,
        boxShadow: '0 1px 2px var(--tint-subtle)',
      }}
    >
      {busy ? <Spinner size={18} color="#64748b" /> : <GoogleGlyph />}
      {children}
    </button>
  );
}

function GoogleGlyph() {
  return (
    <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden focusable="false">
      <path
        fill="#4285F4"
        d="M17.64 9.2c0-.64-.06-1.25-.16-1.84H9v3.48h4.84a4.14 4.14 0 0 1-1.8 2.72v2.26h2.92c1.7-1.57 2.68-3.88 2.68-6.62z"
      />
      <path
        fill="#34A853"
        d="M9 18c2.43 0 4.47-.8 5.96-2.18l-2.92-2.26c-.8.54-1.84.86-3.04.86-2.34 0-4.32-1.58-5.03-3.7H.96v2.33A9 9 0 0 0 9 18z"
      />
      <path
        fill="#FBBC05"
        d="M3.97 10.72a5.4 5.4 0 0 1 0-3.44V4.95H.96a9 9 0 0 0 0 8.1l3.01-2.33z"
      />
      <path
        fill="#EA4335"
        d="M9 3.58c1.32 0 2.5.45 3.44 1.35l2.58-2.58C13.47.9 11.43 0 9 0A9 9 0 0 0 .96 4.95l3.01 2.33C4.68 5.16 6.66 3.58 9 3.58z"
      />
    </svg>
  );
}

function OrDivider({ label }: { readonly label: string }) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        color: 'var(--color-on-surface-muted)',
      }}
    >
      <span aria-hidden style={{ flex: 1, height: 1, background: 'var(--color-border)' }} />
      <span style={{ fontSize: 'var(--text-meta)', fontWeight: 500 }}>{label}</span>
      <span aria-hidden style={{ flex: 1, height: 1, background: 'var(--color-border)' }} />
    </div>
  );
}

function SubmitButton({
  submitting,
  children,
}: {
  readonly submitting: boolean;
  readonly children: ReactNode;
}) {
  return (
    <button
      type="submit"
      disabled={submitting}
      style={{
        marginTop: 'var(--space-1)',
        background: submitting
          ? 'var(--color-primary, #0d7a4f)'
          : 'linear-gradient(135deg, var(--color-primary, #0d7a4f) 0%, #16956c 100%)',
        color: 'var(--color-primary-fg, #ffffff)',
        padding: '15px 22px',
        borderRadius: 'var(--radius-md)',
        fontWeight: 600,
        fontSize: 'var(--text-body)',
        fontFamily: 'inherit',
        border: 'none',
        cursor: submitting ? 'wait' : 'pointer',
        opacity: submitting ? 0.85 : 1,
        boxShadow: '0 1px 2px rgba(13,122,79,0.4), 0 8px 20px rgba(13,122,79,0.24)',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 10,
        minHeight: 'var(--touch-target)',
      }}
    >
      {submitting ? <Spinner /> : null}
      {children}
    </button>
  );
}

function ErrorAlert({ children }: { readonly children: ReactNode }) {
  return (
    <div
      role="alert"
      style={{
        padding: '10px 14px',
        borderRadius: 'var(--radius-sm, 12px)',
        background: 'rgba(239,68,68,0.06)',
        border: '1px solid rgba(239,68,68,0.22)',
        color: 'var(--color-tone-error, #991b1b)',
        fontSize: 'var(--text-meta)',
        lineHeight: 1.5,
      }}
    >
      {children}
    </div>
  );
}

function Divider() {
  return <div aria-hidden style={{ height: 1, background: 'var(--color-border)' }} />;
}

const linkButtonStyle: React.CSSProperties = {
  background: 'transparent',
  border: 'none',
  padding: 0,
  color: 'var(--color-primary, #0d7a4f)',
  fontSize: 'var(--text-meta)',
  fontWeight: 500,
  fontFamily: 'inherit',
  cursor: 'pointer',
  justifySelf: 'end',
  textAlign: 'right',
  lineHeight: 1.4,
};

const inlineLinkStyle: React.CSSProperties = {
  color: 'var(--color-primary, #0d7a4f)',
  fontWeight: 600,
  textDecoration: 'none',
};

const footerLineStyle: React.CSSProperties = {
  margin: 0,
  textAlign: 'center',
  fontSize: 'var(--text-meta)',
  color: 'var(--color-on-surface-muted)',
  lineHeight: 1.5,
};
